import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

/**
 * BatchProgressDialog - Display real-time progress for batch AI summary generation
 * 
 * Shows:
 * - Overall progress bar
 * - Current batch status
 * - Statistics (completed, failed, rate per hour)
 * - Recent completions with school names
 * - Failed schools list with names
 * - Estimated time remaining
 * - Processing details (drive times, summary types)
 */
const BatchProgressDialog = ({ progress, open, onClose }) => {
  if (!progress || !open) return null;

  const {
    isActive,
    totalSchools,
    completed,
    failed,
    verificationFailed = 0,
    verificationFailedSchools = [],
    currentBatch,
    totalBatches,
    startTime,
    avgTimePerSchool,
    estimatedSecondsRemaining,
    failedSchools = [],
    recentCompletions = [],
    driveTimesGenerated = [5, 10, 15],
    summaryTypesGenerated = ['geographic', 'competitive', 'projections', 'combined'],
    mode,
    teamName,
    error,
    lastProcessedSchool
  } = progress;

  // Calculate stats
  const totalProcessed = completed + failed + verificationFailed;
  const successRate = totalProcessed > 0 ? ((completed / totalProcessed) * 100).toFixed(1) : 0;
  const progressPercent = totalSchools > 0 ? ((totalProcessed / totalSchools) * 100).toFixed(1) : 0;
  
  // Time calculations
  const elapsedSeconds = startTime ? (Date.now() - startTime) / 1000 : 0;
  const elapsedMinutes = (elapsedSeconds / 60).toFixed(1);
  const schoolsPerHour = elapsedSeconds > 0 ? ((completed / elapsedSeconds) * 3600).toFixed(0) : 0;
  
  // ETA formatting
  const formatETA = (seconds) => {
    if (!seconds || seconds <= 0) return 'Calculating...';
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m`;
  };

  const title = mode === 'team' && teamName
    ? `Generate for Team: ${teamName}`
    : 'Batch Generation Progress';

  return (
    <Dialog
      open={open}
      onClose={isActive ? () => {} : onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isActive}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{title}</Typography>
          <Chip
            icon={<ScheduleIcon />}
            label={isActive ? 'Running' : 'Completed'}
            color={isActive ? 'primary' : 'success'}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Overall Progress Bar */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="textSecondary">
              Overall Progress
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {isActive && totalProcessed === 0
                ? `Processing ${totalSchools} schools...`
                : `${completed + failed + verificationFailed} / ${totalSchools} (${progressPercent}%)`}
            </Typography>
          </Box>
          {isActive && lastProcessedSchool && (
            <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 0.5 }}>
              Last processed: {lastProcessedSchool.school_name || lastProcessedSchool.school_uuid}
              {lastProcessedSchool.status && ` (${lastProcessedSchool.status})`}
            </Typography>
          )}
          <LinearProgress
            variant={isActive && totalProcessed === 0 ? 'indeterminate' : 'determinate'}
            value={parseFloat(progressPercent) || 0}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>

        {/* Batch Status (hide when team mode and single batch) */}
        {totalBatches > 1 && (
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Current Batch: {currentBatch} / {totalBatches}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              Generating {summaryTypesGenerated.length} summary types for drive times: {driveTimesGenerated.join(', ')} min
            </Typography>
          </Box>
        )}

        {/* Statistics Grid */}
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(140px, 1fr))" gap={2} mb={3}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
            <Box display="flex" alignItems="center">
              <CheckCircleIcon sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h4">{completed}</Typography>
                <Typography variant="caption">Fully Processed</Typography>
              </Box>
            </Box>
          </Paper>

          {verificationFailed > 0 && (
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <Box display="flex" alignItems="center">
                <WarningIcon sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h4">{verificationFailed}</Typography>
                  <Typography variant="caption">Quality Issues</Typography>
                </Box>
              </Box>
            </Paper>
          )}

          <Paper elevation={0} sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
            <Box display="flex" alignItems="center">
              <ErrorIcon sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h4">{failed}</Typography>
                <Typography variant="caption">Failed</Typography>
              </Box>
            </Box>
          </Paper>

          {!isActive && totalProcessed > 0 && (
            <>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.100' }}>
                <Box>
                  <Typography variant="h5">{schoolsPerHour}</Typography>
                  <Typography variant="caption" color="textSecondary">Schools/Hour</Typography>
                </Box>
              </Paper>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.100' }}>
                <Box>
                  <Typography variant="h5">{avgTimePerSchool?.toFixed(1)}s</Typography>
                  <Typography variant="caption" color="textSecondary">Avg Time/School</Typography>
                </Box>
              </Paper>
            </>
          )}
        </Box>

        {/* Time Stats */}
        {totalProcessed > 0 && (
          <Box mb={3}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="body2">Elapsed</Typography>
                  <Typography variant="h6">{elapsedMinutes} min</Typography>
                </Box>
                {estimatedSecondsRemaining > 0 && (
                  <Box textAlign="right">
                    <Typography variant="body2">ETA</Typography>
                    <Typography variant="h6">{formatETA(estimatedSecondsRemaining)}</Typography>
                  </Box>
                )}
                <Box textAlign="right">
                  <Typography variant="body2">Success Rate</Typography>
                  <Typography variant="h6">{successRate}%</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Fully Processed (Successful) */}
        {recentCompletions.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom color="success.main">
              Processed – Summary data verified ({recentCompletions.length} schools)
            </Typography>
            <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
              {recentCompletions.slice(0, 15).map((completion, idx) => (
                <ListItem key={idx} dense>
                  <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                  <ListItemText
                    primary={completion.school_name || completion.school_uuid || 'Unknown'}
                    secondary={completion.school_uuid && completion.school_name ? completion.school_uuid : null}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption', fontFamily: 'monospace', fontSize: '0.7rem' }}
                  />
                </ListItem>
              ))}
              {recentCompletions.length > 15 && (
                <ListItem dense>
                  <ListItemText
                    primary={`... and ${recentCompletions.length - 15} more`}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        )}

        {/* Verification Failed – Generated but data quality issues */}
        {verificationFailedSchools.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom color="warning.main">
              Quality Issues – Generated but missing expected data (e.g. county, competitor research)
            </Typography>
            <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'warning.light', borderRadius: 1, p: 1 }}>
              {verificationFailedSchools.map((item, idx) => (
                <ListItem key={idx} dense>
                  <WarningIcon fontSize="small" sx={{ mr: 1, color: 'warning.dark' }} />
                  <ListItemText
                    primary={item.school_name || item.school_uuid || 'Unknown'}
                    secondary={(item.verification?.issues || []).join(', ') || 'See details'}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Failed Schools */}
        {failedSchools.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom color="error">
              Failed Schools ({failedSchools.length})
            </Typography>
            <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'error.light', borderRadius: 1, p: 1 }}>
              {failedSchools.map((failed, idx) => (
                <ListItem key={idx} dense>
                  <ErrorIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                  <ListItemText
                    primary={failed.school_name || failed.school_uuid || 'Unknown'}
                    secondary={failed.error || 'Unknown error'}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
            <Box mt={1}>
              <Button
                size="small"
                onClick={() => {
                  const failedData = failedSchools.map(f => ({
                    name: f.school_name,
                    uuid: f.school_uuid,
                    error: f.error
                  }));
                  const blob = new Blob([JSON.stringify(failedData, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `failed-schools-${new Date().toISOString()}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Export Failed Schools
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!isActive && (
          <Button onClick={onClose} color="primary" variant="contained">
            Close
          </Button>
        )}
        {isActive && (
          <Typography variant="caption" color="textSecondary" sx={{ mr: 2 }}>
            Processing... Do not close this window
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BatchProgressDialog;
